import React from 'react';
import { formatCurrency } from '@koob/utils';
import AdditionalServicesBand, {
  AdditionalServicesBandScopeEnum,
  AdditionalServicesBandStateEnum,
} from '../AdditionalServicesBand';
import type { Translate } from './types';

interface TripDayExperienceDayBandProps {
  dayExtrasCount: number;
  dayAvailableCount: number;
  daySelectedExtras: any[];
  sumExtrasPrice: (extras: any[]) => number;
  extrasCurrency: (extras: any[]) => string;
  extrasAmountPerPax: (extras: any[]) => number | null;
  renderServiceChips: (extras: any[], scope: any) => React.ReactNode;
  // Absent on a read-only day: the band lists its services, nothing more.
  onAction?: () => void;
  t: Translate;
}

function getBandState(dayExtrasCount: number, dayAvailableCount: number) {
  if (dayExtrasCount > 0) return AdditionalServicesBandStateEnum.SELECTED;

  return dayAvailableCount > 0
    ? AdditionalServicesBandStateEnum.AVAILABLE
    : AdditionalServicesBandStateEnum.UNAVAILABLE;
}

export default function TripDayExperienceDayBand({
  dayExtrasCount,
  dayAvailableCount,
  daySelectedExtras,
  sumExtrasPrice,
  extrasCurrency,
  extrasAmountPerPax,
  renderServiceChips,
  onAction,
  t,
}: TripDayExperienceDayBandProps) {
  const perPaxAmount = extrasAmountPerPax(daySelectedExtras);
  return (
    <AdditionalServicesBand
      scope={AdditionalServicesBandScopeEnum.DAY}
      state={getBandState(dayExtrasCount, dayAvailableCount)}
      title={t('compose.services.dayTitle')}
      subtitle={
        dayExtrasCount > 0
          ? t('compose.services.daySelected', {
              count: dayExtrasCount,
            })
          : t('compose.services.dayAvailable', {
              count: dayAvailableCount,
            })
      }
      emptyLabel={t('compose.services.dayEmpty')}
      totalLabel={t('compose.services.amount', {
        price: formatCurrency({
          amount: sumExtrasPrice(daySelectedExtras),
          currency: extrasCurrency(daySelectedExtras),
        }),
      })}
      perPaxLabel={
        perPaxAmount != null
          ? t('compose.services.perPax', {
              price: formatCurrency({
                amount: perPaxAmount,
                currency: extrasCurrency(daySelectedExtras),
              }),
            })
          : undefined
      }
      actionLabel={
        onAction
          ? dayExtrasCount > 0
            ? t('compose.services.edit')
            : t('compose.services.add')
          : undefined
      }
      onAction={onAction}
    >
      {renderServiceChips(
        daySelectedExtras,
        AdditionalServicesBandScopeEnum.DAY,
      )}
    </AdditionalServicesBand>
  );
}
